// =============================================================================
// src/views/camadas/camadas-intensity.ts — Marcadores de intensidade da Tela 05.
//
// Proveniencia: assets/unified-app.js Sections.camadas._addIntensityMarkers
//   (linhas 1474-1494) e o trecho de remocao em deactivate (1496-1500). Cada zona
//   ganha um rotulo (V↑/E↑/...) no centro, deslocado +0.005 em latitude, com o
//   mesmo HTML/estilo inline do original (background da zona, borda branca, sombra).
//
// Desvio FIEL ao contrato (NAO ao codigo): o legado le `mapCtrl.zoneCentroids[key]`
//   — campo privado no MapController TS, fora do contrato publico. Recalculamos o
//   centroide a partir de `mapCtrl.bairrosLayer` (que ESTA no contrato), pela mesma
//   regra de MapController.buildZoneCentroids (bounds por zona -> getCenter()),
//   produzindo o MESMO ponto. Assim mantemos paridade de posicao sem alargar o
//   contrato nem tocar o core.
//
// Leaflet: L.marker + L.divIcon (interactive:false) e map.removeLayer — superficie
//   verificada via Context7 (/leaflet/leaflet) em 2026-05-30, identica a usada em
//   src/core/map-controller.ts.
//
// SRP: so a camada de marcadores-rotulo de intensidade. Sem listeners, sem presets.
// =============================================================================

import L from 'leaflet';
import type { Map as LeafletMap, Marker, LatLng, LatLngBounds, GeoJSON as LeafletGeoJSON } from 'leaflet';
import type { ZonaKey, HexColor, MapController, BairroFeatureProperties } from '../../app/types';
import { MANAUS_ZONA_KEY_MAP, MANAUS_ZONA_DISPLAY_NAME } from '../../data';
import {
  INTENSITY_LABEL_BY_ZONE,
  INTENSITY_COLOR_BY_ZONE,
  INTENSITY_FALLBACK_COLOR,
  INTENSITY_FALLBACK_LABEL,
} from './camadas-presets';

/** Deslocamento de latitude do rotulo acima do centroide (legado: centroid.lat + 0.005). */
const INTENSITY_LAT_OFFSET = 0.005;

/** Feature de bairro tipada (properties.ZONAS) — mesma forma usada pelo MapController. */
type BairroLayer = LeafletGeoJSON & { feature: GeoJSON.Feature<GeoJSON.Geometry, BairroFeatureProperties> };

/** Resolve a ZonaKey de uma feature de bairro (fallback 'norte'), igual ao core. */
function zonaKeyOfLayer(layer: BairroLayer): ZonaKey {
  return MANAUS_ZONA_KEY_MAP[layer.feature.properties.ZONAS] ?? 'norte';
}

/**
 * Centroide de cada zona a partir dos bounds dos bairros (replica
 * MapController.buildZoneCentroids). Acumula os bounds por zona e devolve o centro.
 */
function computeZoneCentroids(bairrosLayer: LeafletGeoJSON): Partial<Record<ZonaKey, LatLng>> {
  const zoneBounds = new Map<ZonaKey, LatLngBounds>();
  bairrosLayer.eachLayer((layer) => {
    const key = zonaKeyOfLayer(layer as BairroLayer);
    const bounds = zoneBounds.get(key) ?? L.latLngBounds([]);
    bounds.extend((layer as LeafletGeoJSON).getBounds());
    zoneBounds.set(key, bounds);
  });
  const centroids: Partial<Record<ZonaKey, LatLng>> = {};
  zoneBounds.forEach((bounds, key) => { centroids[key] = bounds.getCenter(); });
  return centroids;
}

/** HTML inline do rotulo de intensidade (verbatim de _addIntensityMarkers, 1487). */
function intensityIconHtml(fill: HexColor, label: string): string {
  return (
    `<div style="background:${fill};color:white;font-size:10px;font-weight:600;` +
    'padding:3px 6px;border-radius:4px;border:1.5px solid white;' +
    `box-shadow:0 1px 3px rgba(0,0,0,0.2);white-space:nowrap">${label}</div>`
  );
}

/** Cria o marcador-rotulo (divIcon nao-interativo) de uma zona em seu centroide. */
function buildIntensityMarker(key: ZonaKey, centroid: LatLng): Marker {
  /* v8 ignore next 2 -- key vem de MANAUS_ZONA_DISPLAY_NAME; ambos Record<ZonaKey> cobrem todas as 6 zonas; fallbacks inalcançáveis */
  const fill = INTENSITY_COLOR_BY_ZONE[key] ?? INTENSITY_FALLBACK_COLOR;
  const label = INTENSITY_LABEL_BY_ZONE[key] ?? INTENSITY_FALLBACK_LABEL;
  const icon = L.divIcon({ className: '', html: intensityIconHtml(fill, label), iconSize: [0, 0] });
  return L.marker([centroid.lat + INTENSITY_LAT_OFFSET, centroid.lng], { icon, interactive: false });
}

/**
 * Adiciona os 6 rotulos de intensidade ao mapa e devolve os marcadores criados,
 * para o controlador guardar e remover no deactivate. No-op se o mapa/camada de
 * bairros ainda nao montaram (fetch GeoJSON assincrono) — preserva o guard do core.
 */
export function addIntensityMarkers(mapCtrl: MapController): Marker[] {
  const map = mapCtrl.map;
  if (!map || !mapCtrl.bairrosLayer) return [];
  const centroids = computeZoneCentroids(mapCtrl.bairrosLayer);
  const created: Marker[] = [];
  (Object.keys(MANAUS_ZONA_DISPLAY_NAME) as ZonaKey[]).forEach((key) => {
    const centroid = centroids[key];
    if (!centroid) return;
    const marker = buildIntensityMarker(key, centroid);
    marker.addTo(map);
    created.push(marker);
  });
  return created;
}

/** Remove os marcadores de intensidade do mapa (replica o loop de deactivate, 1498). */
export function removeIntensityMarkers(map: LeafletMap | null, markers: readonly Marker[]): void {
  if (!map) return;
  markers.forEach((marker) => map.removeLayer(marker));
}
